import { useLayoutEffect, useState, RefObject } from 'react';

interface IUseStickyOffsetOptions {
  sticky?: boolean;
  marginTop?: number;
}

interface IUseStickyOffsetReturn {
  topOffset: number;
  isOverflowing: boolean;
}

/**
 * Hook to calculate top offset for sticky elements.
 * Ensures the element can stick and shows the bottom items when overflowing the viewport.
 * @param ref - ref to the target element
 * @param options.sticky - enable or disable sticky behavior
 * @param options.marginTop - additional top margin (e.g., header)
 * @returns { topOffset, isOverflowing }
 */
export const useStickyOffset = (
  ref: RefObject<HTMLElement>,
  { sticky = true, marginTop = 0 }: IUseStickyOffsetOptions = {}
): IUseStickyOffsetReturn => {
  const [topOffset, setTopOffset] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateOffset = () => {
    if (!ref.current || !sticky) {
      setTopOffset(0);
      setIsOverflowing(false);
      return;
    }

    const elementHeight = ref.current.offsetHeight;
    const viewHeight = window.innerHeight - marginTop;

    if (elementHeight <= viewHeight) {
      setTopOffset(0);
      setIsOverflowing(false);
    } else {
      setTopOffset(viewHeight - elementHeight);
      setIsOverflowing(true);
    }
  };

  useLayoutEffect(() => {
    updateOffset();
    requestAnimationFrame(updateOffset); // ensures after layout shifts
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, [sticky, marginTop]);

  return { topOffset, isOverflowing };
};
